// Dev-only modal shown before writing a device/project preset into the repo
// `assets/` folder. Captures the pricing tier (free/premium → target subfolder);
// for project presets it also captures gallery metadata (tags, description, featured).

import {useState} from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  FormControl,
  Checkbox,
  Box,
} from '@mui/material'
import {useTranslation} from 'react-i18next'

export type PresetTier = 'free' | 'premium'

export interface PresetMeta {
  tier: PresetTier
  tags?: string[]
  description?: string
  featured?: boolean
}

interface SavePresetDialogProps {
  open: boolean
  kind: 'device' | 'project'
  onConfirm: (meta: PresetMeta) => void
  onCancel: () => void
}

const SavePresetDialog = ({open, kind, onConfirm, onCancel}: SavePresetDialogProps) => {
  const {t} = useTranslation()
  const [tier, setTier] = useState<PresetTier>('premium')
  const [tags, setTags] = useState('')
  const [description, setDescription] = useState('')
  const [featured, setFeatured] = useState(true)

  // Fields reset by remounting (parent passes a changing `key` per open) — no reset effect.
  const handleConfirm = () => {
    if (kind === 'device') {
      onConfirm({tier})
      return
    }
    const parsedTags = tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean)
    onConfirm({tier, tags: parsedTags, description: description.trim() || undefined, featured})
  }

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle sx={sx.title}>
        {kind === 'device' ? t('toolbar.presetTitleDevice') : t('toolbar.presetTitleProject')}
      </DialogTitle>
      <DialogContent>
        <FormControl sx={sx.field}>
          <FormLabel sx={sx.label}>{t('toolbar.presetPricing')}</FormLabel>
          <RadioGroup row value={tier} onChange={(e) => setTier(e.target.value as PresetTier)}>
            <FormControlLabel value="free" control={<Radio size="small" />} label={t('toolbar.presetFree')} />
            <FormControlLabel value="premium" control={<Radio size="small" />} label={t('toolbar.presetPremium')} />
          </RadioGroup>
        </FormControl>

        {kind === 'project' && (
          <Box sx={sx.projectFields}>
            <TextField
              fullWidth
              size="small"
              label={t('toolbar.presetTags')}
              placeholder={t('toolbar.presetTagsPlaceholder')}
              value={tags}
              onChange={(e) => setTags(e.target.value)}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              label={t('toolbar.presetDescription')}
              placeholder={t('toolbar.presetDescriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={featured} onChange={(e) => setFeatured(e.target.checked)} />}
              label={t('toolbar.presetFeatured')}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t('toolbar.cancel')}</Button>
        <Button onClick={handleConfirm} variant="contained">
          {t('toolbar.presetSave')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const sx = {
  title: {
    fontSize: 16,
    fontWeight: 600,
  },
  field: {
    mt: 1,
    width: '100%',
  },
  label: {
    fontSize: 13,
    mb: 0.5,
  },
  projectFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mt: 2,
  },
}

export default SavePresetDialog
